import { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { IMG_HOOK } from "@/lib/mock";

const MATERIALS = [
  {
    id: "laminate",
    label: "Laminate",
    tagline: "Classic Wood Look",
    heading: "The affordable premium finish.",
    description:
      "Laminate delivers the look of real hardwood at a fraction of the cost. Its ultra-hard wear layer resists scratches, fading, and everyday wear better than actual wood — a smart choice for high-traffic areas.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/4wknacka_IMG_8732.jpeg",
    features: [
      "Scratch & fade resistant",
      "Wide range of wood-look styles",
      "Cost-effective vs. hardwood",
      "Quick, clean installation",
    ],
    priceRange: "$3–$5 / sq ft (labor)",
    bestFor: "Living rooms · Bedrooms · Hallways · Dining areas",
  },
  {
    id: "lvp",
    label: "LVP",
    tagline: "Luxury Vinyl Plank",
    heading: "Waterproof. Durable. Beautiful.",
    description:
      "LVP is our most-requested flooring — and for good reason. It's 100% waterproof, incredibly durable, and looks nearly indistinguishable from real hardwood. Perfect for kitchens, basements, bathrooms, and homes with kids or pets.",
    image: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/ud9ci9kn_2F372B81-94D2-480F-B9D3-8B7BE4993A4D.png",
    features: [
      "100% waterproof",
      "Scratch & dent resistant",
      "Realistic wood-look textures",
      "Warm underfoot, kid & pet friendly",
    ],
    priceRange: "$3–$6 / sq ft (labor)",
    bestFor: "Kitchens · Basements · Bathrooms · Living areas",
  },
  {
    id: "tile",
    label: "Tile",
    tagline: "Timeless & Custom",
    heading: "Craftsmanship you can feel.",
    description:
      "Tile is where our precision really shines. From large-format marble-look tile to intricate hex mosaics, every cut is scored on-site for tight grout lines and flush transitions. Perfect for bathrooms, kitchens, and statement floors that last decades.",
    image: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/9z6zytzm_IMG_7768.jpeg",
    features: [
      "Fully waterproof",
      "Custom patterns (hex, marble, mosaic)",
      "Lifetime durability",
      "Premium finish for bathrooms & kitchens",
    ],
    priceRange: "$7–$12 / sq ft (labor)",
    bestFor: "Bathrooms · Kitchen backsplash · Entryways · Showers",
  },
];

export default function FlooringMaterials() {
  const [active, setActive] = useState("laminate");
  const material = MATERIALS.find((m) => m.id === active);

  return (
    <section
      id="materials"
      data-testid="materials-section"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14 lg:mb-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
            <span className="h-px w-8 bg-orange-600" />
            Flooring Materials
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
            Three finishes.
            <br />
            <span className="text-stone-500">One obsessive standard.</span>
          </h2>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-2xl">
            Whether it's waterproof LVP, budget-smart laminate, or timeless
            tile — we install every material with the same level of precision.
            Explore what works for your space.
          </p>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          className="flex flex-wrap gap-2 mb-10 border-b border-stone-200"
        >
          {MATERIALS.map((m) => {
            const isActive = m.id === active;
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={isActive}
                data-testid={`material-tab-${m.id}`}
                onClick={() => setActive(m.id)}
                className={`relative px-5 md:px-6 py-3.5 text-sm md:text-base font-semibold transition-colors ${
                  isActive
                    ? "text-stone-900"
                    : "text-stone-500 hover:text-stone-800"
                }`}
              >
                {m.label}
                <span className="ml-2 text-xs font-normal text-stone-400 hidden sm:inline">
                  · {m.tagline}
                </span>
                {isActive && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-orange-600 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div
          data-testid={`material-panel-${active}`}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center"
        >
          {/* Image */}
          <div className="relative overflow-hidden rounded-2xl bg-stone-100 shadow-[0_15px_50px_-20px_rgba(0,0,0,0.25)]">
            <img
              src={material.image}
              alt={`M&L Exterior and Interior Design — ${material.tagline} flooring installation`}
              loading="lazy"
              className="w-full h-full object-cover aspect-[4/3]"
            />
          </div>

          {/* Content */}
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-orange-600 font-semibold mb-3">
              {material.tagline}
            </div>
            <h3 className="font-display font-semibold text-3xl md:text-4xl leading-tight tracking-[-0.02em] text-stone-900">
              {material.heading}
            </h3>
            <p className="mt-5 text-stone-600 text-[16px] leading-[1.7]">
              {material.description}
            </p>

            {/* Discount callout */}
            <div
              data-testid="materials-discount-banner"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50/70 px-5 py-4"
            >
              <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-orange-600 text-white font-bold text-sm">
                %
              </span>
              <div>
                <div className="font-display font-semibold text-stone-900 text-[15px] leading-tight">
                  Ask us about our 20% discount on materials
                </div>
                <div className="mt-1 text-[13px] text-stone-600">
                  Save on {material.label.toLowerCase()} materials when you
                  book your install through M&amp;L.
                </div>
              </div>
            </div>

            <ul className="mt-7 space-y-3">
              {material.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check
                    size={18}
                    className="text-orange-600 mt-0.5 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-stone-700 text-[15px]">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                  Labor cost
                </div>
                <div className="mt-1 font-semibold text-stone-900">
                  {material.priceRange}
                </div>
              </div>
              <div className="rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                  Best for
                </div>
                <div className="mt-1 text-sm text-stone-800 font-medium">
                  {material.bestFor}
                </div>
              </div>
            </div>

            <a
              href="#contact"
              data-testid={`material-cta-${material.id}`}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-stone-900 text-stone-50 text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors duration-300 group"
            >
              Get a {material.label} Quote
              <ArrowRight
                size={15}
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
